import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import * as uuid from 'uuid';

import { Scenario, Projection, PlanningFormStatus, PlanningEntity } from '../models';

// This service is provided in app.module.ts
// due to use in modal component
@Injectable()

export class PlanningDataService {

  private scenarioSubject = new BehaviorSubject<Scenario>({});
  private scenariosSubject: Subject<Scenario[]> = new Subject<Scenario[]>();

  getScenarioSavedObservable(): Observable<Scenario[]> {
    return this.scenariosSubject.asObservable();
  }

  scenarioSaved(): void {
    this.scenariosSubject.next();
  }

  getCurrentScenarioValue(): Scenario {
    return this.scenarioSubject.getValue();
  }

  getScenarioObservable(): Observable<Scenario> {
    return this.scenarioSubject.asObservable();
  }

  setConnectionId(connectionId: string) {
    let scenario = this.scenarioSubject.getValue();
    scenario.wsConnectionId = connectionId;

    this.updateScenario(scenario);
  }

  updateScenario(scenario: Scenario) {
    this.scenarioSubject.next(scenario);
  }

  createNewProjection(entityData) {
    const scenario: Scenario = this.scenarioSubject.getValue();
    const primaryEntity: PlanningEntity = JSON.parse(JSON.stringify(scenario.projections[0].srcEntity));

    if (scenario.projections[0].dstEntity === null) {
      // This is only for the 1st entity created after the primary was created
      scenario.projections[0].dstEntity = entityData;
    } else {
      primaryEntity.isTestedParty = null;
      primaryEntity.testedPartyId = uuid.v4();
      primaryEntity.financials = {
        currencyId: 1,
        operatingExpense: null,
        cogs: null,
        sales: null,
        grossProfit: null,
        operatingProfit: null,
        netCostPlus: null,
        operatingMargin: null
      };
      primaryEntity.transactionTypeTitle = null;
      primaryEntity.jurisdiction.taxRate = null;

      const newProjection: Projection = {
        projectionId: uuid.v4(),
        compSearchId: null,
        transactionValue: null,
        transactionType: null,
        currencyId: 1,
        isTestedParty: null,
        isPrimaryEntity: null,
        characterization: {
          valueAddedDistributor: null,
          sicCode: null
        },
        classifications: {
          selectedClassifications: null
        },
        // For now we will assume that the new entity is the dst
        srcEntity: primaryEntity,
        dstEntity: entityData,
        riskScale: null,
        riskValues: {
          aggressiveLevel: 0,
          auditHistory: 0,
          documentationLevel: 0
        },
        formStatus: {
          transactionAndTestedPartyForm: false,
          financialDetialsForm: false,
          taxRateForm: false,
          riskSettingsForm: false,
          projectionReady: false
        }
      };
      scenario.projections.push(newProjection);
    }
    this.updateScenario(scenario);
  }

  updateEntityFinancials(projectionId: string, bothEntites) {
    const scenario: Scenario = this.scenarioSubject.getValue();

    const currentProjectionObj = this.getCurrentProjection(projectionId);

    if (currentProjectionObj.currentProjection.srcEntity.entityName === bothEntites.primaryEntity.entityName) {
      currentProjectionObj.currentProjection.srcEntity = bothEntites.primaryEntity;
      currentProjectionObj.currentProjection.dstEntity = bothEntites.tradingPartner;
    } else {
      currentProjectionObj.currentProjection.srcEntity = bothEntites.tradingPartner;
      currentProjectionObj.currentProjection.dstEntity = bothEntites.primaryEntity;
    }

    scenario.projections.splice(currentProjectionObj.currentProjectionIndex, 1, currentProjectionObj.currentProjection);
    this.updateScenario(scenario);
  }

  updateFormValues(currentFormStatus: PlanningFormStatus, projectionId: string) {
    const currentProjection: Projection = this.getCurrentProjection(projectionId).currentProjection;
    currentProjection.formStatus = currentFormStatus;

    this.updateProjection(projectionId, currentProjection);
  }

  updateRiskLevels(projectionId, riskValues) {
    const scenario: Scenario = this.scenarioSubject.getValue();
    const currentProjectionObj = this.getCurrentProjection(projectionId);
    let currentProjection: Projection = currentProjectionObj.currentProjection;

    currentProjection.riskValues = riskValues;

    const avg = (riskValues.aggressiveLevel + riskValues.auditHistory + riskValues.documentationLevel) / 3;

    currentProjection.riskScale = Math.round(avg);

    scenario.projections.splice(currentProjectionObj.currentProjectionIndex, 1, currentProjection);
    this.updateScenario(scenario);
  }

  updateProjection(projectionId, projection) {
    const scenario: Scenario = this.scenarioSubject.getValue();

    const currentProjectionObj = this.getCurrentProjection(projectionId);

    let currentProjection: Projection = currentProjectionObj.currentProjection;

    currentProjection = projection;
    scenario.projections.splice(currentProjectionObj.currentProjectionIndex, 1, currentProjection);
    this.updateScenario(scenario);
  }

  updateProjectionWithResultData(respData) {
    const currentProjection: Projection = this.getCurrentProjection(respData.projectionId).currentProjection;

    currentProjection.srcResults = respData.srcResults;
    currentProjection.dstResults = respData.dstResults;
    currentProjection.destinationRange = respData.projection.destinationRange;
    currentProjection.sourceRange = respData.projection.sourceRange;
    this.updateProjection(currentProjection.projectionId, currentProjection);
  }

  allResultsReturned(): boolean {
    const scenario: Scenario = this.scenarioSubject.getValue();
    let stillWaitingStatus = false;

    scenario.projections.forEach((projection) => {
      if (!projection.srcResults && !projection.dstResults) {
        stillWaitingStatus = true;
      }
    });
    return stillWaitingStatus;
  }

  getCurrentProjection(projectionId) {
    const scenario: Scenario = this.scenarioSubject.getValue();
    let currentProjection: Projection;
    let currentProjectionIndex: number;

    scenario.projections.forEach((projection, index) => {
      if (projection.projectionId === projectionId) {
        currentProjection = projection;
        currentProjectionIndex = index;
      }
    });

    return { currentProjection, currentProjectionIndex };
  }

  createNewEntity(entityData) {
    const entity: PlanningEntity = {
      entityName: entityData.entityName,
      isTestedParty: null,
      isPrimaryEntity: entityData.isPrimaryEntity,
      testedPartyId: uuid.v4(),
      financials: {
        currencyId: 1,
        operatingExpense: null,
        cogs: null,
        sales: null,
        grossProfit: null,
        operatingProfit: null,
        netCostPlus: null,
        operatingMargin: null
      },
      domicileId: entityData.country.Id,
      jurisdiction: {
        taxRate: null,
        id: entityData.country.Id,
        jurisdictionId: entityData.country.JurisdictionId,
        isoCode: entityData.country.IsoCode,
        name: entityData.country.Name,
        currencyIsoCode: entityData.country.CurrencyIsoCode
      },
      transactionTypeTitle: null
    };

    if (entity.isPrimaryEntity) {
      this.createPrimaryEntity(entity);
    } else {
      this.createNewProjection(entity);
    }
  }

  createPrimaryEntity(entity) {
    const scenario: Scenario = this.scenarioSubject.getValue();

    scenario.projections = [{
      projectionId: uuid.v4(),
      compSearchId: null,
      transactionValue: null,
      transactionType: null,
      characterization: {
        valueAddedDistributor: null,
        sicCode: null
      },
      classifications: {
        selectedClassifications: null
      },
      currencyId: 1,
      srcEntity: entity,
      dstEntity: null,
      riskValues: {
        aggressiveLevel: 0,
        auditHistory: 0,
        documentationLevel: 0
      },
      riskScale: null,
    }];
    this.updateScenario(scenario);
  }

  createScenario(scenarioName: string) {
    const scenario: Scenario = {
      name: scenarioName,
      planningScenarioId: uuid.v4(),
      wsConnectionId: this.scenarioSubject.getValue().wsConnectionId || null,
      projections: null
    };

    this.updateScenario(scenario);
  }

  allProjectionsReady(): boolean {
    const scenario: Scenario = this.scenarioSubject.getValue();
    if (scenario.projections) {
      return scenario.projections.every(projection => projection.formStatus && projection.formStatus.projectionReady);
    }
  }

  scenarioHasAResult(): boolean {
    const scenario: Scenario = this.scenarioSubject.getValue();

    if (scenario.projections) {
      return scenario.projections.some(projection => !!projection.dstResults || !!projection.srcResults);
    }
  }
}
